import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import { generateAvatar, getAttendee, getTicket } from '@apiCalls';
import { CtaContainer, InputField } from '@components';
import { useAppContext } from '@contexts';
import { isValidEmail } from '@utils';
import {
  handleRenderError,
  withErrorBoundary,
  withErrorHandler,
} from '@utils/errorHandler';

interface IAttendeeDetailsFormProperties {
  onComplete: (guestDetails: IAttendee) => void;
  attendeeTicket: IAttendeeTicket;
}

export const AttendeeDetailsForm = (
  properties: IAttendeeDetailsFormProperties
) => {
  try {
    const { event } = useAppContext();
    const [blocked, setBlocked] = useState(false);
    const [loading, setLoading] = useState(false);
    const [detailsRequired, setDetailsRequired] = useState(false);
    const [guestDetails, setGuestDetails] = useState<IAttendee>({
      Email: '',
      FirstName: '',
      ID: properties.attendeeTicket.AttendeeID || 0,
      LastName: '',
      Phone: '',
    });
    const { data: existingAttendee, isError: existingAttendeeError } = useQuery(
      {
        queryKey: ['attendee', properties.attendeeTicket.AttendeeID],
        queryFn: () =>
          properties.attendeeTicket.AttendeeID
            ? getAttendee(properties.attendeeTicket.AttendeeID)
            : undefined,
      }
    );
    const { data: ticketData, isError: ticketDataError } = useQuery({
      queryKey: ['ticket', properties.attendeeTicket.TicketID],
      queryFn: () =>
        properties.attendeeTicket.TicketID
          ? getTicket(properties.attendeeTicket.TicketID)
          : undefined,
    });

    const checkFormEntriesValid = useCallback(() => {
      const trimmedFirstName = guestDetails?.FirstName?.trim() ?? '';
      const trimmedLastName = guestDetails?.LastName?.trim() ?? '';

      if (
        !/^.{2,}$/.test(trimmedFirstName) ||
        !/^.{2,}$/.test(trimmedLastName) ||
        !isValidEmail(guestDetails.Email)
      ) {
        return setBlocked(false);
      }
      return setBlocked(true);
    }, [guestDetails]);

    const getDetailsRequired = async () => {
      if (ticketData && !ticketDataError) {
        setDetailsRequired(ticketData.AttendeeDetailsRequired);
      }
    };

    const getGuestDetails = () => {
      if (
        existingAttendee &&
        !existingAttendeeError &&
        properties.attendeeTicket.AttendeeID
      ) {
        let displayAttendeeDetails = true;
        if (
          existingAttendee.LastName &&
          /\([1-9]\)$/.test(existingAttendee.LastName)
        ) {
          displayAttendeeDetails = false;
        }
        setGuestDetails({
          Email: displayAttendeeDetails ? existingAttendee.Email : '',
          FirstName: displayAttendeeDetails ? existingAttendee.FirstName : '',
          ID: properties.attendeeTicket.AttendeeID,
          LastName: displayAttendeeDetails ? existingAttendee.LastName : '',
          Phone: displayAttendeeDetails ? existingAttendee.Phone : '',
        });
      } else {
        setGuestDetails({
          Email: '',
          FirstName: '',
          ID: properties.attendeeTicket.AttendeeID || 0,
          LastName: '',
          Phone: '',
        });
      }
    };

    useEffect(() => {
      withErrorHandler(getDetailsRequired)();
    }, [ticketData]);

    useEffect(() => {
      withErrorHandler(getGuestDetails)();
    }, [existingAttendee]);

    useEffect(
      withErrorBoundary(() => {
        checkFormEntriesValid();
      }),
      [checkFormEntriesValid]
    );

    const handleComplete = async () => {
      if ((detailsRequired && !blocked) || !event) {
        return;
      }
      setLoading(true);

      let avatar = '';
      try {
        const generatedAvatar = await generateAvatar({
          FirstName: guestDetails.FirstName,
          LastName: guestDetails.LastName,
          event,
        });
        avatar = generatedAvatar || '';
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'AttendeeDetailsForm',
            operation: 'generateAvatar',
          },
        });
        // Continue without avatar - don't block the user
      }

      setLoading(false);
      return properties.onComplete({ ...guestDetails, Photo: avatar || '' });
    };

    return (
      <>
        <form
          className='formGuestDetails'
          onSubmit={withErrorHandler(uiEvent => {
            uiEvent.preventDefault();
            handleComplete();
          })}
        >
          <div className='form-row form-row-double'>
            <InputField
              type='text'
              id='addAttendeeFirstName'
              name='addAttendeeFirstName'
              label='First Name'
              required
              value={guestDetails.FirstName}
              onChange={uiEvent => {
                const target = uiEvent.target as HTMLTextAreaElement;
                setGuestDetails({
                  ...guestDetails,
                  FirstName: target?.value ?? '',
                });
              }}
            />
            <InputField
              type='text'
              id='addAttendeeLastName'
              name='addAttendeeLastName'
              label='Last Name'
              required
              value={guestDetails.LastName}
              onChange={uiEvent => {
                const target = uiEvent.target as HTMLTextAreaElement;
                setGuestDetails({
                  ...guestDetails,
                  LastName: target?.value ?? '',
                });
              }}
            />
          </div>
          <div className='form-row'>
            <InputField
              type='email'
              id='addAttendeeEmail'
              name='addAttendeeEmail'
              label='Email'
              required
              value={guestDetails.Email}
              onChange={uiEvent => {
                const target = uiEvent.target as HTMLTextAreaElement;
                setGuestDetails({
                  ...guestDetails,
                  Email: target?.value ?? '',
                });
              }}
            />
          </div>
          <div className='formInstructionText'>
            <h4>Please add an email address to send the guest their ticket.</h4>
          </div>
          <input type='submit' hidden />
        </form>
        <CtaContainer
          blocked={detailsRequired ? !blocked : false}
          loading={loading}
          buttonText='Continue'
          onButtonClick={withErrorHandler(handleComplete)}
        />
      </>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};
